import { http } from "../../shared/api/http";

export type PilotFindingType = "BUG" | "UX_FRICTION" | "WORKFLOW_OBSERVATION";
export type PilotFindingSeverity = "CRITICAL" | "HIGH" | "MEDIUM" | "LOW";
export type PilotFindingStatus = "OPEN" | "IN_PROGRESS" | "RESOLVED" | "VERIFIED";

export type PilotFinding = {
  id: string;
  type: PilotFindingType;
  severity: PilotFindingSeverity;
  status: PilotFindingStatus;
  title: string;
  description: string;
  workflowStep: string | null;
  observedRole: string | null;
  resolutionNote: string | null;
  observedAt: string;
  repairOrder: { id: string; roNumber: string; status: string } | null;
  createdBy: { id: string; displayName: string } | null;
};

export type PilotObservation = {
  id: string;
  observedAt: string;
  customerArrivalSeconds: number | null;
  coreWorkflowCompleted: boolean;
  completedWithoutTraining: boolean;
  pageLoadMs: number | null;
  apiResponseMs: number | null;
  notes: string | null;
  repairOrder: {
    id: string;
    roNumber: string;
    status: string;
    customer: { firstName: string | null; lastName: string | null; companyName: string | null };
    vehicle: { year: number; make: string; model: string };
  };
  observedBy: { id: string; displayName: string } | null;
};

export type PilotRepairOrder = {
  id: string;
  roNumber: string;
  status: string;
  openedAt: string;
  customer: { firstName: string | null; lastName: string | null; companyName: string | null };
  vehicle: { year: number; make: string; model: string };
  pilotObservation: { id: string } | null;
};

export type PilotDashboard = {
  release: string;
  status: string;
  release2Blocked: boolean;
  metrics: {
    operatingDays: number;
    consecutiveOperatingDays: number;
    realRepairEvents: number;
    criticalOpen: number;
    arrivalSamples: number;
    arrivalWithinTarget: number;
    arrivalSuccessRate: number;
    completedCoreWorkflow: number;
    completedWithoutTraining: number;
  };
  performance: {
    arrivalMedianSeconds: number | null;
    arrivalP95Seconds: number | null;
    pageLoadMedianMs: number | null;
    pageLoadP95Ms: number | null;
    apiMedianMs: number | null;
    apiP95Ms: number | null;
  };
  findingCounts: Array<{ type: PilotFindingType; total: number; open: number }>;
  criteria: {
    consecutiveOperatingDays: boolean;
    realRepairEvents: boolean;
    noCriticalBugs: boolean;
    customerArrival: boolean;
    unassistedCoreWorkflow: boolean;
  };
};

export type CreateFindingPayload = {
  type: PilotFindingType;
  severity: PilotFindingSeverity;
  title: string;
  description: string;
  repairOrderId?: string;
  workflowStep?: string;
  observedRole?: string;
};

export type ObservationPayload = {
  repairOrderId: string;
  customerArrivalSeconds?: number;
  coreWorkflowCompleted: boolean;
  completedWithoutTraining: boolean;
  pageLoadMs?: number;
  apiResponseMs?: number;
  notes?: string;
};

export const pilotValidationApi = {
  dashboard: async () => (await http.get<PilotDashboard>("/v1/pilot-validation/dashboard")).data,
  findings: async () => (await http.get<PilotFinding[]>("/v1/pilot-validation/findings")).data,
  observations: async () => (await http.get<PilotObservation[]>("/v1/pilot-validation/observations")).data,
  repairOrders: async () => (await http.get<PilotRepairOrder[]>("/v1/pilot-validation/repair-orders")).data,
  createFinding: async (payload: CreateFindingPayload) =>
    (await http.post<PilotFinding>("/v1/pilot-validation/findings", payload)).data,
  updateFinding: async (id: string, status: PilotFindingStatus, resolutionNote?: string) =>
    (await http.patch<PilotFinding>(`/v1/pilot-validation/findings/${id}/status`, { status, resolutionNote })).data,
  recordObservation: async (payload: ObservationPayload) =>
    (await http.post<PilotObservation>("/v1/pilot-validation/observations", payload)).data
};
